import { debounce } from '@/entrypoints/utils';
import { createLogger } from '@/lib/logger';
import { CartService } from '../internals/cart-service';
import { CartStore } from '../internals/cart-store';
import { CartCommandController } from '../internals/command-controller';
import { CartEvents, TypedCartEvent } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { CartFetchInterceptor } from '../internals/fetch-interceptor';
import { SimpleCartService } from '../internals/simple-cart-service';

/**
 * Normalizes an unknown cart failure value into a user-facing message.
 *
 * @param error Value thrown or emitted by cart code.
 * @returns A message safe to display in the mini-cart error UI.
 */
function toUserErrorMessage(error: unknown): string {
  if (typeof error === 'string' && error.trim() !== '') {
    return error;
  }

  if (error instanceof Error && error.message.trim() !== '') {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const description = (error as { description?: unknown }).description;
    if (typeof description === 'string' && description.trim() !== '') return description;

    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  }

  return 'Something went wrong updating your cart. Please try again.';
}

/**
 * Root mini-cart web component.
 *
 * Wires together the cart service, fetch interceptor, command controller, and store.
 * Exposes a small public API:
 * - `bus`: shared `CartEventBus` instance for children to subscribe/emit.
 * - `cartApi`: simplified programmatic API for imperative interactions.
 * - `store`: reactive store syncing cart state via events and service.
 */
export class MiniCart extends HTMLElement {
  private service: CartService;
  private interceptor: CartFetchInterceptor;
  private commands: CartCommandController;
  private initalised: boolean = false;
  private onReadyQueue: (() => void)[] = [];

  private emitDebouncedChange: (detail: TypedCartEvent<'cart:changed'>['detail']) => void;

  private logger = createLogger('mini-cart');

  private openOnAdd: boolean = true;

  private errorRoot: HTMLElement | null = null;

  // Public API
  /** Simplified cart API wrapper for imperative use-cases. */
  cartApi: SimpleCartService;
  /** Event bus for all mini-cart events. */
  bus: CartEventBus;
  /** Store maintaining cart state and helpers. */
  store: CartStore;

  /**
   * Construct the mini-cart and initialize core subsystems.
   */
  constructor() {
    super();
    this.bus = new CartEventBus();
    this.service = new CartService(this.bus);
    this.interceptor = new CartFetchInterceptor(this.bus);
    this.cartApi = new SimpleCartService(this.service);
    this.commands = new CartCommandController(this.service, this.cartApi);
    this.store = new CartStore(this.bus, this.service);

    this.emitDebouncedChange = debounce((detail: TypedCartEvent<'cart:changed'>['detail']) => {
      this.bus.emit(CartEvents.DebouncedChange, {
        cart: detail.cart,
        reason: detail.reason,
      });
    }, 250);

    this.errorRoot = this.querySelector('mini-cart-error');
  }

  openMiniCart() {
    this.bus.emit(CartEvents.UIUpdate, { action: 'open' });
    this.bus.emit(CartEvents.UIOpened);
    this.setAttribute('data-open', 'true');
  }

  closeMiniCart() {
    this.bus.emit(CartEvents.UIUpdate, { action: 'close' });
    this.bus.emit(CartEvents.UIClosed);
    this.removeAttribute('data-open');
  }

  setLoading(state: boolean) {
    this.classList.toggle('is-loading', state);
  }

  showError(error: string) {
    const errorPopover = this.errorRoot?.querySelector('.error-popover');

    if (!errorPopover) {
      this.logger.error('Error popover not found');
      return;
    }

    const errorPopoverMessageElem = errorPopover?.querySelector('.error-message');

    if (!errorPopoverMessageElem) {
      this.logger.error('Error message element not found');
      return;
    }

    errorPopoverMessageElem.textContent = error;
    this.errorRoot?.setAttribute('data-open', 'true');
  }

  closeError() {
    this.errorRoot?.setAttribute('data-open', 'false');
  }

  bindEvents() {
    if (this.openOnAdd) this.bus.on(CartEvents.AddSuccess, this.openMiniCart.bind(this));

    this.bus.on(CartEvents.Changed, (event: TypedCartEvent<'cart:changed'>) => {
      this.emitDebouncedChange(event.detail);

      if (
        event.detail.cart.items.length == 1 &&
        event.detail.cart.items[0].handle == 'shipping-protection'
      ) {
        this.cartApi.removeByKey(event.detail.cart.items[0].key!);
      }

      if (
        !event.detail.cart.items.find(
          (item) =>
            item.handle != 'shipping-protection' &&
            !Object.keys(item.properties).includes('__free_gift'),
        ) &&
        event.detail.cart.items.length > 0
      ) {
        this.cartApi.clear();
        return;
      }
    });

    this.bus.on(CartEvents.UIOpen, this.openMiniCart.bind(this));
    this.bus.on(CartEvents.UIClose, this.closeMiniCart.bind(this));

    this.bus.onMany(
      [CartEvents.AddStart, CartEvents.UpdateStart, CartEvents.ClearStart],
      this.setLoading.bind(this, true),
    );

    // Ensure that loading is cleared on any success/error
    this.bus.onMany(
      [
        CartEvents.AddSuccess,
        CartEvents.AddError,
        CartEvents.UpdateSuccess,
        CartEvents.UpdateError,
        CartEvents.ClearSuccess,
        CartEvents.ClearError,
        CartEvents.Changed,
        CartEvents.Error,
      ],
      this.setLoading.bind(this, false),
    );

    this.bus.on(CartEvents.Error, (event: TypedCartEvent<'cart:error'>) =>
      this.showError(toUserErrorMessage(event.detail.error)),
    );

    this.errorRoot?.addEventListener('click', this.closeError.bind(this));
  }

  unbindEvents() {
    this.bus.off(CartEvents.UIOpen, this.openMiniCart.bind(this));
    this.bus.off(CartEvents.UIClose, this.closeMiniCart.bind(this));
  }

  isInitialised() {
    return this.initalised;
  }

  execOnReady(fn: () => void) {
    if (this.initalised) {
      fn();
    } else {
      this.onReadyQueue.push(fn);
    }
  }

  /**
   * Lifecycle: attach subsystems and start intercepting/command handling.
   */
  connectedCallback() {
    this.interceptor.start();
    this.commands.start();

    this.bindEvents();
    this.initalised = true;
    this.bus.emit(CartEvents.Ready);
    this.onReadyQueue.forEach((fn) => fn());
    this.onReadyQueue = [];
  }

  /**
   * Lifecycle: detach subsystems and stop intercepting/command handling.
   */
  disconnectedCallback() {
    this.commands.stop();
    this.interceptor.stop();

    this.unbindEvents();
    this.initalised = false;
  }
}

if (!customElements.get('mini-cart')) {
  customElements.define('mini-cart', MiniCart);
}
